/**
 * フライトの要約。
 *
 * パイロットモードでは先頭3項目だけを見せる想定なので、
 * **飛行中に知りたい順**に並べてある。
 */

import { toUtm, utmDistance, type FixSample } from '@gv4d/core';
import { metersToFeet } from '../units.ts';
import { toZonedTime, type TimeZoneId } from './timezone.ts';

export interface SummaryRow {
  label: string;
  value: string;
}

export interface FlightSummary {
  rows: SummaryRow[];
  maxAlt: number;
  durationSec: number;
  distanceKm: number;
}

export function summarize(fixes: FixSample[], tz: TimeZoneId): FlightSummary {
  if (fixes.length === 0) {
    return { rows: [], maxAlt: 0, durationSec: 0, distanceKm: 0 };
  }

  const first = fixes[0];
  const last = fixes[fixes.length - 1];
  const durationSec = (last.t - first.t) / 1000;

  const alts = fixes.map((f) => f.altPressure ?? f.altMsl).filter((a): a is number => a != null);
  const maxAlt = alts.length ? Math.max(...alts) : 0;
  const minAlt = alts.length ? Math.min(...alts) : 0;

  const speeds = fixes.map((f) => f.speed).filter((s): s is number => s != null);
  const maxSpeed = speeds.length ? Math.max(...speeds) : 0;

  // 航跡長。UTM 平面で足し上げる（会場規模なら歪みは無視できる）
  let distance = 0;
  for (let i = 1; i < fixes.length; i++) {
    distance += utmDistance(
      toUtm(fixes[i - 1].lon, fixes[i - 1].lat),
      toUtm(fixes[i].lon, fixes[i].lat),
    );
  }

  const vs = fixes.map((f) => f.verticalSpeed).filter((v): v is number => v != null);
  const maxClimb = vs.length ? Math.max(...vs) : 0;
  const maxSink = vs.length ? Math.min(...vs) : 0;

  const accuracy = fixes.map((f) => f.accuracy).filter((a): a is number => a != null);
  const medianAccuracy = accuracy.length
    ? [...accuracy].sort((a, b) => a - b)[Math.floor(accuracy.length / 2)]
    : null;

  const rows: SummaryRow[] = [
    { label: '最高高度', value: `${Math.round(metersToFeet(maxAlt))} ft` },
    { label: '飛行時間', value: formatDuration(durationSec) },
    { label: '航跡長', value: `${(distance / 1000).toFixed(1)} km` },
    {
      label: '高度範囲',
      value: `${Math.round(metersToFeet(minAlt))} – ${Math.round(metersToFeet(maxAlt))} ft`,
    },
    { label: '最大対地速度', value: `${(maxSpeed * 3.6).toFixed(1)} km/h` },
    { label: '最大上昇 / 下降', value: `${maxClimb.toFixed(1)} / ${maxSink.toFixed(1)} m/s` },
    { label: '測位点', value: `${fixes.length.toLocaleString()} 点` },
    {
      label: '時刻',
      value: (() => {
        const z = toZonedTime(first.t, tz);
        return `${z.dateTime} ${z.zone}`;
      })(),
    },
  ];

  if (medianAccuracy !== null) {
    rows.push({ label: 'GPS精度（中央値）', value: `${medianAccuracy.toFixed(1)} m` });
  }

  return { rows, maxAlt, durationSec, distanceKm: distance / 1000 };
}

function formatDuration(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return h > 0 ? `${h}時間${m}分` : `${m}分`;
}
